/**
 * List View Plugin for Native Interactive Site Map (ISM)
 *
 * Orchestrates the listing sheet panel: header (title), tab bar, and scrollable content area.
 * Writes into [data-panel] regardless of desktop sidebar or mobile drawer context.
 *
 * What to show per active chip (tab bar on/off, panel content) is not decided here — it's
 * delegated to services/listViewContentService.js, keyed by chip. This plugin only owns the
 * panel's shared DOM lifecycle (mount/unmount, header, tabs) and wires it to that content.
 *
 * Responsibilities:
 * - Inject listing header, tab bar, and content wrapper into [data-panel]
 * - Wire tab click events to Redux dispatch (tabSelected action)
 * - Subscribe to store$ to keep tab counts, active state, and the header title in sync
 * - Clean up on stop
 *
 * @module nativeInteractiveSiteMap/plugins/listView.plugin
 */

import { EMPTY } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map } from 'rxjs/operators';
import createListingHeader from '../components/listingHeader.dom';
import createListingContent from '../components/listingContent.dom';
import {
  selectFilteredHousingUnits,
  selectFilteredHousePlans,
  selectSelectedTab,
  selectHousePlanCount,
  selectHousingUnitCount,
  selectActiveView,
  selectActiveChip,
  selectSelectedLotId,
  selectIsLotInventorySettled,
} from '../redux/selectors';
import getListingTitle from '../services/listingTitleService';
import getListViewContent from '../services/listViewContentService';
import createTabsDom from '../components/tabs.dom';
import createTabInteractionHandlers from '../handlers/tabInteractionHandlers';
import { TAB_FLOOR_PLANS, TAB_QUICK_MOVE_INS, VIEW_LISTING } from '../constants';

export default function createListViewPlugin() {
  const state = {
    header: null,
    tabs: null,
    content: null,
    panel: null,
    store$: null,
    getState: null,
    dispatch: null,
    dataActions: null,
    subscriptions: [],
  };

  function renderCards(cards, renderGrid) {
    if (!state.content) {
      return;
    }

    /*
     * Centralizes the "empty vs still loading" decision for all renderers.
     * Delay renderGrid until lot inventory has settled to avoid premature empty states.
     */
    if ((!Array.isArray(cards) || cards.length === 0) && !selectIsLotInventorySettled(state.getState())) {
      return;
    }
    
    state.content.render(renderGrid(cards));
  }

  const contentContext = { renderCards };

  function getActiveContent(storeState) {
    return getListViewContent(selectActiveChip(storeState));
  }

  function onHousePlansChanged() {
    const currentState = state.getState();
    getActiveContent(currentState).renderOnHousePlansChanged(currentState, contentContext);
  }

  function onHousingUnitsChanged() {
    const currentState = state.getState();
    getActiveContent(currentState).renderOnHousingUnitsChanged(currentState, contentContext);
  }

  function onSelectedLotChanged() {
    const currentState = state.getState();
    getActiveContent(currentState).renderOnSelectedLotChanged(currentState, contentContext);
  }

  function createTabs(currentState) {
    const floorPlansLabel = 'Floor plans';
    const quickMoveInsLabel = 'Quick move-ins';

    /*
     * Counts read as 0 until the lot-inventory fetch settles. That's not a real "0 results", so the
     * badge stays blank rather than showing a misleading 0.
     * The gated subscription in setupSubscriptions fills in the real count once settled.
    */
    const isSettled = selectIsLotInventorySettled(currentState);

    state.tabs = createTabsDom([
      {
        label: floorPlansLabel,
        count: isSettled ? selectHousePlanCount(currentState) : '',
        tabValue: TAB_FLOOR_PLANS,
        isActive: selectSelectedTab(currentState) === TAB_FLOOR_PLANS,
      },
      {
        label: quickMoveInsLabel,
        count: isSettled ? selectHousingUnitCount(currentState) : '',
        tabValue: TAB_QUICK_MOVE_INS,
        isActive: selectSelectedTab(currentState) === TAB_QUICK_MOVE_INS,
      },
    ]);

    state.tabs.attachEventListeners(
      createTabInteractionHandlers((tabValue) => state.dispatch(state.dataActions.tabSelected(tabValue)))
    );

    state.panel.insertBefore(state.tabs.getElement(), state.content.getElement());
  }

  function removeTabs() {
    state.tabs?.remove();
    state.tabs = null;
  }

  function render() {
    const currentState = state.getState();

    state.header = createListingHeader();
    state.content = createListingContent();

    state.panel.appendChild(state.header.getElement());
    state.panel.appendChild(state.content.getElement());

    if (getActiveContent(currentState).hasTabs) {
      createTabs(currentState);
    }

    state.header.setText(getListingTitle(selectActiveChip(currentState)));
    getActiveContent(currentState).renderContent(currentState, contentContext);
  }

  function remove() {
    state.header?.remove();
    state.tabs?.remove();
    state.content?.remove();
    state.header = null;
    state.tabs = null;
    state.content = null;
  }

  function onListingViewActiveChanged(isListingViewActive) {
    if (isListingViewActive) {
      render();
    } else {
      remove();
    }
  }

  function onSelectedTabChanged(selectedTab) {
    const currentState = state.getState();
    state.tabs.setActiveTab(selectedTab);
    getActiveContent(currentState).renderContent(currentState, contentContext);
  }

  function onActiveChipChanged(activeChip) {
    const currentState = state.getState();
    const tabsEnabled = getActiveContent(currentState).hasTabs;
    const hasTabs = state.tabs != null;

    state.header?.setText(getListingTitle(activeChip));

    if (tabsEnabled && !hasTabs) {
      createTabs(currentState);
    } else if (!tabsEnabled && hasTabs) {
      removeTabs();
    }

    getActiveContent(currentState).renderContent(currentState, contentContext);
  }

  function mapToTabCounts(storeState) {
    return {
      floorPlans: selectHousePlanCount(storeState),
      quickMoveIns: selectHousingUnitCount(storeState),
    };
  }

  function tabCountsEqual(previous, current) {
    return previous.floorPlans === current.floorPlans && previous.quickMoveIns === current.quickMoveIns;
  }

  function onTabCountsChanged({ floorPlans, quickMoveIns }) {
    state.tabs.updateCounts({
      [TAB_FLOOR_PLANS]: floorPlans,
      [TAB_QUICK_MOVE_INS]: quickMoveIns,
    });
  }

  function setupSubscriptions() {
    state.subscriptions.push(
      state.store$.pipe(
        filter(() => state.content != null),
        map(selectFilteredHousePlans),
        distinctUntilChanged(),
        catchError(() => EMPTY)
      ).subscribe(onHousePlansChanged),

      state.store$.pipe(
        filter(() => state.content != null),
        map(selectFilteredHousingUnits),
        distinctUntilChanged(),
        catchError(() => EMPTY)
      ).subscribe(onHousingUnitsChanged),

      state.store$.pipe(
        filter(() => state.content != null),
        map(selectSelectedLotId),
        distinctUntilChanged(),
        catchError(() => EMPTY)
      ).subscribe(onSelectedLotChanged),

      state.store$.pipe(
        map((storeState) => selectActiveView(storeState) === VIEW_LISTING),
        distinctUntilChanged(),
      ).subscribe(onListingViewActiveChanged),

      state.store$.pipe(
        filter(() => state.tabs != null),
        map(selectSelectedTab),
        distinctUntilChanged(),
      ).subscribe(onSelectedTabChanged),

      state.store$.pipe(
        filter(() => state.content != null),
        map(selectActiveChip),
        distinctUntilChanged(),
      ).subscribe(onActiveChipChanged),

      state.store$.pipe(
        filter((storeState) => state.tabs != null && selectIsLotInventorySettled(storeState)),
        map(mapToTabCounts),
        distinctUntilChanged(tabCountsEqual),
      ).subscribe(onTabCountsChanged),
    );
  }

  return {
    name: 'list-view',

    async init(context) {
      context.state.listView = {
        started: false,
        ready: false,
      };
    },

    async start(context) {
      if (context.state.listView?.started) {
        return;
      }

      state.panel = context.root.querySelector('[data-panel]');
      if (!state.panel) {
        return;
      }

      context.state.listView = { started: true, ready: false };

      const { getState, dispatch, dataActions, store$ } = context.services;
      state.getState = getState;
      state.dispatch = dispatch;
      state.dataActions = dataActions;
      state.store$ = store$;

      setupSubscriptions();

      context.state.listView.ready = true;
    },

    async stop(context) {
      state.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
      state.subscriptions = [];
      state.header?.remove();
      state.tabs?.remove();
      state.content?.remove();
      state.header = null;
      state.tabs = null;
      state.content = null;
      state.panel = null;
      state.store$ = null;
      state.getState = null;
      state.dispatch = null;
      state.dataActions = null;
      context.state.listView = { started: false, ready: false };
    },
  };
}